import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Edit3, ExternalLink, Swords, X, Save, AlertTriangle } from "lucide-react";

const INPUT_CLS = "w-full bg-[var(--input-bg)] border border-[var(--border)] focus:border-[var(--border-strong)] focus:outline-none px-3 py-2 text-[13px] text-[var(--text-primary)]";

function Field({ label, children, hint }) {
  return (
    <div>
      <label className="block text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">{label}</label>
      {children}
      {hint && <div className="text-[10px] font-mono text-zinc-500 mt-1">{hint}</div>}
    </div>
  );
}

function BrandPicker({ selected, onChange, testIdPrefix = "brand" }) {
  const q = useQuery({
    queryKey: ["products-brief"],
    queryFn: () => api.get("/products").then(r => r.data),
  });
  const items = q.data || [];
  const toggle = (id) => {
    const next = selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id];
    onChange(next);
  };
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}-picker`}>
      {items.length === 0 && <span className="text-[11px] text-zinc-500 font-mono">no brands yet</span>}
      {items.map(p => {
        const on = selected.includes(p.id);
        return (
          <button
            type="button"
            key={p.id}
            onClick={() => toggle(p.id)}
            data-testid={`${testIdPrefix}-chip-${p.id}`}
            className={`text-[11px] font-mono uppercase tracking-widest px-2.5 py-1 border ${on ? "bg-[#FF3B30] border-[#FF3B30] text-white" : "border-white/10 text-zinc-400 hover:border-white/30"}`}
          >
            {p.name}
          </button>
        );
      })}
    </div>
  );
}

function CompetitorForm({ initial, onCancel, onSaved }) {
  const [form, setForm] = useState({
    name: initial?.name || "",
    landing_url: initial?.landing_url || "",
    weakness_points: (initial?.weakness_points || []).join("\n"),
    notes: initial?.notes || "",
    product_ids: initial?.product_ids || [],
  });

  const save = useMutation({
    mutationFn: (body) => initial ? api.patch(`/competitors/${initial.id}`, body) : api.post("/competitors", body),
    onSuccess: () => { toast.success(initial ? "Competitor updated" : "Competitor added"); onSaved?.(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const submit = () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    save.mutate({
      name: form.name.trim(),
      landing_url: form.landing_url.trim(),
      weakness_points: form.weakness_points.split("\n").map(x => x.trim()).filter(Boolean),
      notes: form.notes.trim(),
      product_ids: form.product_ids,
    });
  };

  const set = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e.target.value }));

  return (
    <div className="border border-[var(--border)] bg-[var(--surface)] p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-black text-2xl tracking-tight">{initial ? "Edit competitor." : "New competitor."}</h3>
        {onCancel && (
          <button onClick={onCancel} data-testid="cancel-competitor-btn" className="p-1.5 border border-[var(--border)] hover:border-[var(--border-strong)]">
            <X className="w-4 h-4"/>
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Name *"><input className={INPUT_CLS} data-testid="competitor-name" value={form.name} onChange={set("name")}/></Field>
        <Field label="Landing URL"><input className={`${INPUT_CLS} font-mono`} placeholder="https://…" data-testid="competitor-url" value={form.landing_url} onChange={set("landing_url")}/></Field>
      </div>
      <Field label="Weakness points (one per line)" hint="These will be seeded into orchestrated campaigns as talking points. Framed as personal experience, never as accusation.">
        <textarea rows={5} className={INPUT_CLS} data-testid="competitor-weaknesses"
                  placeholder={"slow support tickets\nno refund policy\nphone-only signup"}
                  value={form.weakness_points} onChange={set("weakness_points")}/>
      </Field>
      <Field label="Notes (internal)" hint="Anything else Jarvis should know about them.">
        <textarea rows={3} className={INPUT_CLS} data-testid="competitor-notes" value={form.notes} onChange={set("notes")}/>
      </Field>
      <Field label="Belongs to brand(s)" hint="Leave empty to make this competitor available for any brand. Pick one or more to scope orchestration.">
        <BrandPicker selected={form.product_ids} onChange={(v) => setForm(p => ({...p, product_ids: v}))} testIdPrefix="competitor-brand"/>
      </Field>
      <button
        onClick={submit}
        disabled={save.isPending}
        data-testid="save-competitor-btn"
        className="w-full bg-[#FF3B30] hover:bg-[#FF5A50] text-white font-medium py-2.5 flex items-center justify-center gap-2 disabled:opacity-50"
      >
        {save.isPending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>}
        {initial ? "SAVE CHANGES" : "ADD COMPETITOR"}
      </button>
    </div>
  );
}

export default function Competitors() {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  const q = useQuery({
    queryKey: ["competitors"],
    queryFn: () => api.get("/competitors").then(r => r.data),
  });

  const del = useMutation({
    mutationFn: (id) => api.delete(`/competitors/${id}`),
    onSuccess: () => { toast.success("Removed"); qc.invalidateQueries({queryKey:["competitors"]}); },
  });

  const refresh = () => {
    qc.invalidateQueries({queryKey:["competitors"]});
    setCreating(false); setEditing(null);
  };

  return (
    <Layout title="Competitors." subtitle="Who Jarvis will subtly undermine"
      right={!creating && !editing && (
        <button onClick={() => setCreating(true)} data-testid="new-competitor-btn"
          className="bg-[#FF3B30] hover:bg-[#FF5A50] text-white font-medium px-3 py-2 flex items-center gap-2 text-[13px]">
          <Plus className="w-4 h-4"/> New competitor
        </button>
      )}
    >
      <div className="mb-6 border border-amber-500/30 bg-amber-500/5 p-4 flex gap-3 items-start" data-testid="competitor-disclaimer">
        <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5"/>
        <div className="text-[12px] text-amber-200 leading-relaxed">
          Weakness points are fed into campaign scripts as <b>lived user frustration</b>, not accusation.
          Jarvis will only name a competitor in at most one post per campaign and paraphrase the rest.
          Keep points factual and verifiable.
        </div>
      </div>

      {creating && <div className="mb-6"><CompetitorForm onCancel={() => setCreating(false)} onSaved={refresh}/></div>}
      {editing && <div className="mb-6"><CompetitorForm initial={editing} onCancel={() => setEditing(null)} onSaved={refresh}/></div>}

      <div className="grid md:grid-cols-2 gap-4">
        {q.isLoading && <div className="col-span-2 text-zinc-500 text-sm p-6">Loading…</div>}
        {q.data?.length === 0 && !creating && (
          <div className="col-span-2 border border-dashed border-white/15 p-12 text-center bg-dots" data-testid="competitors-empty">
            <Swords className="w-8 h-8 text-zinc-700 mx-auto mb-4"/>
            <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mb-2">// no competitors tracked</div>
            <div className="text-zinc-400 text-sm">Add competitors and their weak spots. Jarvis will feed those into orchestrated campaigns as organic talking points.</div>
          </div>
        )}
        {q.data?.map((c) => (
          <div key={c.id} data-testid={`competitor-card-${c.id}`} className="border border-white/10 p-5 hover:border-white/25">
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="font-display font-black text-xl tracking-tight text-white">{c.name}</div>
                {c.landing_url && (
                  <a href={c.landing_url} target="_blank" rel="noreferrer"
                     className="font-mono text-[11px] text-zinc-500 hover:text-[#FF3B30] flex items-center gap-1 mt-1 truncate">
                    {c.landing_url} <ExternalLink className="w-3 h-3 shrink-0"/>
                  </a>
                )}
              </div>
              <div className="flex gap-1">
                <button onClick={() => setEditing(c)} data-testid={`edit-competitor-${c.id}`}
                        className="p-1.5 border border-white/10 hover:border-white/30 text-zinc-400 hover:text-white">
                  <Edit3 className="w-3.5 h-3.5"/>
                </button>
                <button onClick={() => { if (window.confirm(`Remove ${c.name}?`)) del.mutate(c.id); }}
                        data-testid={`delete-competitor-${c.id}`}
                        className="p-1.5 border border-white/10 hover:border-[#FF3B30] text-zinc-400 hover:text-[#FF3B30]">
                  <Trash2 className="w-3.5 h-3.5"/>
                </button>
              </div>
            </div>
            {(c.weakness_points || []).length > 0 && (
              <div className="mt-3">
                <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 mb-1">weak spots</div>
                <ul className="space-y-1">
                  {c.weakness_points.slice(0,6).map((w,i) => (
                    <li key={`${c.id}-w-${i}-${w}`} className="text-[12px] text-zinc-300 flex gap-2">
                      <span className="text-[#FF3B30]">›</span>
                      <span>{w}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {c.notes && (
              <div className="mt-3 pt-3 border-t border-white/5 text-[11px] font-mono text-zinc-500 leading-relaxed">
                {c.notes}
              </div>
            )}
          </div>
        ))}
      </div>
    </Layout>
  );
}
